/**
 * Configure → Routing rules (`/partner/routing`, package B2): who gets the
 * first offer, how long each contractor has to accept, how far the offer
 * cascades, who is copied when it moves on, what each urgency means, and the
 * ordered rules that pin some leads to named contractors.
 *
 * What the settings DO is the worker's (route-lead picks the first
 * contractor; the cascade offers the next one when the deadline passes or the
 * pro declines, and the earlier brief link stops working). This screen only
 * says so in plain words and sends what the API validates — it offers exactly
 * the two strategies the worker implements, and a rule's only action is
 * "send to these contractors".
 *
 * Defaults are labelled, never silently saved: a partner who has not saved
 * sees "FinderlyFix defaults". When no per-urgency deadline is saved, the
 * form STARTS with FinderlyFix's suggestion (Emergency 15 · Urgent 30 ·
 * Routine 60) filled in and marked "Suggested — not saved yet", so a partner
 * sees that Emergency need not wait as long as Routine (third live review,
 * 2026-09-27: every urgency got the same 30 minutes); it is saved only with
 * Save routing, and "Clear suggestions" empties it. "Now:" under each field is
 * the SAVED value, never what is being typed. Save is disabled with nothing
 * to save, like Widget settings' Save draft.
 *
 * Layout: panels in one column, two/three-column grids that collapse at phone
 * width (`.grid-2` / `.grid-3`), each rule its own card. Colours come from the
 * design tokens, so light and dark both work.
 */
import { useId, useState, type FormEvent } from 'react';
import { can } from '../auth/roles';
import { useSession } from '../auth/session';
import {
  LEAD_URGENCIES,
  type LeadUrgency,
  type RoutingPatch,
  type RoutingResponse,
  type RoutingRuleInput,
  type RoutingStrategy,
  type Vendor,
} from '../api/types';
import { TRADES } from '../config/model';
import { Link, useUnsavedChangesGuard } from '../router';
import { Choices, Empty, ErrorNotice, Field, Loading, PageHead, useToast } from '../ui/kit';
import { useMutation, useQuery } from '../ui/hooks';

const URGENCY_LABEL: Record<LeadUrgency, string> = { emergency: 'Emergency', urgent: 'Urgent', routine: 'Routine' };

function toInt(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  return Number(trimmed);
}

function splitList(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// ── The profile: strategy, deadlines, cascade, escalation, urgency meaning ──

interface ProfileDraft {
  strategy: RoutingStrategy;
  accept: string;
  urgency: Record<LeadUrgency, string>;
  depth: string;
  emails: string;
  meaning: Record<LeadUrgency, string>;
}

/** True when no per-urgency deadline is saved: the form then starts with the suggestion. */
function noUrgencySaved(r: RoutingResponse): boolean {
  return LEAD_URGENCIES.every((u) => !r.urgency_sla_minutes[u]);
}

/** What the form starts with: the saved values, plus the suggested per-urgency deadlines when none are saved. */
function initialDraft(r: RoutingResponse): ProfileDraft {
  const d = draftFrom(r);
  if (!noUrgencySaved(r) || !r.suggested_urgency_sla_minutes) return d;
  return { ...d, urgency: Object.fromEntries(LEAD_URGENCIES.map((u) => [u, String(r.suggested_urgency_sla_minutes[u])])) as Record<LeadUrgency, string> };
}

function draftFrom(r: RoutingResponse): ProfileDraft {
  const urgency = {} as Record<LeadUrgency, string>;
  const meaning = {} as Record<LeadUrgency, string>;
  for (const u of LEAD_URGENCIES) {
    urgency[u] = r.urgency_sla_minutes[u] ? String(r.urgency_sla_minutes[u]) : '';
    meaning[u] = r.urgency_rules.find((d) => d.urgency === u)?.description ?? '';
  }
  return {
    strategy: r.strategy === 'round_robin' ? 'round_robin' : 'preferred_tier_first',
    accept: String(r.accept_sla_minutes),
    urgency,
    depth: String(r.cascade_depth),
    emails: r.escalation.emails.join(', '),
    meaning,
  };
}

/** Validation for the profile draft; `invalid` gates the one Save button. */
function profileIssues(d: ProfileDraft, data: RoutingResponse) {
  const { min, max } = data.limits.sla_minutes;
  const depthLimits = data.limits.cascade_depth;
  const minutesIssue = (text: string, optional: boolean) => {
    if (optional && !text.trim()) return [];
    const n = toInt(text);
    return n !== null && n >= min && n <= max ? [] : [{ path: '', message: `Between ${min} and ${max} minutes.` }];
  };
  const accept = minutesIssue(d.accept, false);
  const urgency = Object.fromEntries(LEAD_URGENCIES.map((u) => [u, minutesIssue(d.urgency[u], true)])) as Record<LeadUrgency, { path: string; message: string }[]>;
  const depthN = toInt(d.depth);
  const depth =
    depthN !== null && depthN >= depthLimits.min && depthN <= depthLimits.max
      ? []
      : [{ path: '', message: `Between ${depthLimits.min} and ${depthLimits.max}.` }];
  const emails = splitList(d.emails);
  const email =
    emails.length > data.limits.escalation_emails
      ? [{ path: '', message: `At most ${data.limits.escalation_emails} addresses.` }]
      : emails.some((e) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e))
        ? [{ path: '', message: 'One of these is not an email address.' }]
        : [];
  const invalid = [accept, depth, email, ...Object.values(urgency)].some((i) => i.length > 0);
  return { accept, urgency, depth, email, invalid };
}

function profilePatch(d: ProfileDraft, data: RoutingResponse): RoutingPatch {
  return {
    strategy: d.strategy,
    accept_sla_minutes: toInt(d.accept) ?? data.accept_sla_minutes,
    cascade_depth: toInt(d.depth) ?? data.cascade_depth,
    urgency_sla_minutes: Object.fromEntries(LEAD_URGENCIES.map((u) => [u, toInt(d.urgency[u])])),
    escalation: { emails: splitList(d.emails) },
    urgency_rules: LEAD_URGENCIES.filter((u) => d.meaning[u].trim()).map((u) => ({ urgency: u, description: d.meaning[u].trim() })),
  };
}

function ProfileSections({ data, d, setD, editable }: { data: RoutingResponse; d: ProfileDraft; setD: (d: ProfileDraft) => void; editable: boolean }) {
  const { min, max } = data.limits.sla_minutes;
  const depthLimits = data.limits.cascade_depth;
  const issues = profileIssues(d, data);
  const suggested = data.suggested_urgency_sla_minutes;
  // What is in force now: the SAVED value, never the field being typed in.
  const savedFor = (u: LeadUrgency) => data.urgency_sla_minutes[u] ?? data.accept_sla_minutes;
  const showingSuggestion =
    noUrgencySaved(data) && LEAD_URGENCIES.every((u) => d.urgency[u] === String(suggested[u]));
  const matchesSuggestion = LEAD_URGENCIES.every((u) => d.urgency[u] === String(suggested[u]));

  return (
    <>
      {!data.configured ? (
        <p className="panel panel-pad small muted" role="note">
          You are on the FinderlyFix defaults: the first contractor has {data.defaults.accept_sla_minutes} minutes to accept, and a lead is offered to at
          most {data.defaults.cascade_depth} contractors before it comes to your inbox as unmatched.
        </p>
      ) : null}

      <section className="panel panel-pad stack" aria-labelledby="rt-first">
        <h2 id="rt-first">Who gets the first offer</h2>
        <p className="small muted" style={{ margin: 0 }}>
          Every contractor has a tier — Preferred, Standard or Overflow — that you set on the Contractors page.
        </p>
        <Choices<RoutingStrategy>
          legend="Order"
          hideLegend
          value={d.strategy}
          onChange={(v) => editable && setD({ ...d, strategy: v })}
          options={[
            {
              value: 'preferred_tier_first',
              title: 'Best tier first',
              description: 'Preferred contractors first, then standard, then overflow. Contractors in the same tier take turns.',
              disabled: !editable,
            },
            {
              value: 'round_robin',
              title: 'Everyone takes turns',
              description: 'Tiers are ignored: every matching contractor gets leads in rotation.',
              disabled: !editable,
            },
          ]}
        />
        <p className="small muted">Only contractors who cover the trade and ZIP, are within their hours and under their daily cap are offered a lead.</p>
      </section>

      <section className="panel panel-pad stack" aria-labelledby="rt-deadline">
        <h2 id="rt-deadline">How long a contractor has to accept</h2>
        <p className="small muted">
          When the deadline passes or the contractor declines, the lead is offered to the next contractor and the first link stops working.
        </p>
        <div className="grid-2">
          <Field label="Default deadline (minutes)" issues={issues.accept} hint={`FinderlyFix default: ${data.defaults.accept_sla_minutes}.`}>
            {(p) => <input {...p} className="input num" type="number" inputMode="numeric" min={min} max={max} value={d.accept} disabled={!editable} onChange={(e) => setD({ ...d, accept: e.target.value })} />}
          </Field>
          <Field label="Offer to at most (contractors)" issues={issues.depth} hint={`After that, the lead comes to your inbox as unmatched. Default: ${data.defaults.cascade_depth}.`}>
            {(p) => <input {...p} className="input num" type="number" inputMode="numeric" min={depthLimits.min} max={depthLimits.max} value={d.depth} disabled={!editable} onChange={(e) => setD({ ...d, depth: e.target.value })} />}
          </Field>
        </div>
        <fieldset className="stack-sm">
          <legend className="label">By urgency</legend>
          <div className="grid-3">
            {LEAD_URGENCIES.map((u) => (
              <Field key={u} label={`${URGENCY_LABEL[u]} (minutes)`} optional issues={issues.urgency[u]} hint={`Now: ${savedFor(u)} min`}>
                {(p) => (
                  <input
                    {...p}
                    className="input num"
                    type="number"
                    inputMode="numeric"
                    min={min}
                    max={max}
                    placeholder="Default"
                    value={d.urgency[u]}
                    disabled={!editable}
                    onChange={(e) => setD({ ...d, urgency: { ...d.urgency, [u]: e.target.value } })}
                  />
                )}
              </Field>
            ))}
          </div>
          {editable && showingSuggestion ? (
            <div className="row suggestion-note" role="note" style={{ flexWrap: 'wrap', gap: 8 }}>
              <span className="tag info">Suggested — not saved yet</span>
              <span className="small muted">
                FinderlyFix suggests {LEAD_URGENCIES.map((u) => `${URGENCY_LABEL[u]} ${suggested[u]}`).join(' · ')} min. Nothing changes until you save.
              </span>
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setD({ ...d, urgency: { emergency: '', urgent: '', routine: '' } })}>
                Clear suggestions
              </button>
            </div>
          ) : editable && !matchesSuggestion ? (
            <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() =>
                  setD({ ...d, urgency: Object.fromEntries(LEAD_URGENCIES.map((u) => [u, String(suggested[u])])) as Record<LeadUrgency, string> })
                }
              >
                Fill in suggested deadlines
              </button>
              <span className="small muted">
                {LEAD_URGENCIES.map((u) => `${URGENCY_LABEL[u]} ${suggested[u]}`).join(' · ')} min — a FinderlyFix suggestion, not applied until you save.
              </span>
            </div>
          ) : null}
        </fieldset>
      </section>

      <section className="panel panel-pad stack" aria-labelledby="rt-escalate">
        <h2 id="rt-escalate">Who is copied when a lead moves on</h2>
        <Field
          label="Escalation emails"
          optional
          issues={issues.email}
          hint="Copied when a lead is offered to the next contractor, and when nobody accepted. Needs an email destination. Separate with commas."
        >
          {(p) => (
            <input {...p} className="input" type="text" inputMode="email" value={d.emails} disabled={!editable} placeholder="oncall@example.com" onChange={(e) => setD({ ...d, emails: e.target.value })} />
          )}
        </Field>
      </section>

      <section className="panel panel-pad stack" aria-labelledby="rt-meaning">
        <h2 id="rt-meaning">What each urgency means for you</h2>
        <p className="small muted">For your team and contractors. The diagnosis sets a lead’s urgency; these notes do not change it.</p>
        <div className="grid-3">
          {LEAD_URGENCIES.map((u) => (
            <Field key={u} label={URGENCY_LABEL[u]} optional>
              {(p) => (
                <textarea
                  {...p}
                  className="input"
                  rows={3}
                  maxLength={280}
                  value={d.meaning[u]}
                  disabled={!editable}
                  placeholder={u === 'emergency' ? 'Active leak, no heat below 40°F' : ''}
                  onChange={(e) => setD({ ...d, meaning: { ...d.meaning, [u]: e.target.value } })}
                />
              )}
            </Field>
          ))}
        </div>
      </section>
    </>
  );
}

// ── Rules: ordered overrides that pin leads to named contractors ────────────

interface RuleDraft {
  key: string;
  trades: string[];
  urgency: LeadUrgency[];
  prefixes: string;
  vendors: string[];
}

let ruleKey = 0;
function newRule(partial: Partial<RuleDraft> = {}): RuleDraft {
  ruleKey += 1;
  return { key: `r${ruleKey}`, trades: [], urgency: [], prefixes: '', vendors: [], ...partial };
}

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

function RuleCard({
  rule,
  index,
  count,
  vendors,
  editable,
  onChange,
  onMove,
  onRemove,
}: {
  rule: RuleDraft;
  index: number;
  count: number;
  vendors: Vendor[];
  editable: boolean;
  onChange: (r: RuleDraft) => void;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
}) {
  const headingId = useId();
  const prefixIssue = splitList(rule.prefixes).some((p) => !/^\d{1,5}$/.test(p)) ? [{ path: '', message: 'ZIP prefixes are 1 to 5 digits.' }] : [];
  const known = new Map(vendors.map((v) => [v.id, v]));
  return (
    <li className="panel panel-pad stack-sm" aria-labelledby={headingId}>
      <div className="row-between">
        <h3 id={headingId}>Rule {index + 1}</h3>
        {editable ? (
          <div className="row" style={{ gap: 4 }}>
            <button type="button" className="btn btn-sm btn-ghost" disabled={index === 0} onClick={() => onMove(-1)} aria-label={`Move rule ${index + 1} up`}>
              ↑
            </button>
            <button type="button" className="btn btn-sm btn-ghost" disabled={index === count - 1} onClick={() => onMove(1)} aria-label={`Move rule ${index + 1} down`}>
              ↓
            </button>
            <button type="button" className="btn btn-sm btn-danger" onClick={onRemove}>
              Remove rule {index + 1}
            </button>
          </div>
        ) : null}
      </div>
      <fieldset className="stack-sm">
        <legend className="label">Trades (none = any)</legend>
        <div className="chips">
          {TRADES.map((t) => (
            <button key={t.slug} type="button" className="chip" disabled={!editable} aria-pressed={rule.trades.includes(t.slug)} onClick={() => onChange({ ...rule, trades: toggle(rule.trades, t.slug) })}>
              {t.label}
            </button>
          ))}
        </div>
      </fieldset>
      <fieldset className="stack-sm">
        <legend className="label">Urgency (none = any)</legend>
        <div className="chips">
          {LEAD_URGENCIES.map((u) => (
            <button key={u} type="button" className="chip" disabled={!editable} aria-pressed={rule.urgency.includes(u)} onClick={() => onChange({ ...rule, urgency: toggle(rule.urgency, u) })}>
              {URGENCY_LABEL[u]}
            </button>
          ))}
        </div>
      </fieldset>
      <Field label="ZIP prefixes" optional issues={prefixIssue} hint="None = any ZIP. For example, ZIP prefix 981 (Seattle) covers every ZIP starting 981.">
        {(p) => <input {...p} className="input input-mono" inputMode="numeric" value={rule.prefixes} disabled={!editable} placeholder="981, 98052" onChange={(e) => onChange({ ...rule, prefixes: e.target.value })} />}
      </Field>
      <fieldset className="stack-sm">
        <legend className="label">
          Send only to <span className="faint">· {rule.vendors.length} selected</span>
        </legend>
        {vendors.length ? (
          <div className="chips">
            {vendors.map((v) => (
              <button key={v.id} type="button" className="chip" disabled={!editable} aria-pressed={rule.vendors.includes(v.id)} onClick={() => onChange({ ...rule, vendors: toggle(rule.vendors, v.id) })}>
                {v.name}
              </button>
            ))}
            {rule.vendors
              .filter((id) => !known.has(id))
              .map((id) => (
                <span key={id} className="tag warn" title={id}>
                  Not on the active roster
                </span>
              ))}
          </div>
        ) : (
          <p className="small muted">Add contractors on the Contractors page first.</p>
        )}
        {rule.vendors.length === 0 ? <p className="small" style={{ color: 'var(--bad-ink)' }}>Pick at least one contractor.</p> : null}
      </fieldset>
    </li>
  );
}

function rulesFromServer(data: RoutingResponse): RuleDraft[] {
  return data.rules.map((r) =>
    newRule({ trades: r.match.trade ?? [], urgency: r.match.urgency ?? [], prefixes: (r.match.postal_prefix ?? []).join(', '), vendors: r.action.vendor_ids }),
  );
}

function rulesBody(rules: RuleDraft[]): RoutingRuleInput[] {
  return rules.map((r) => ({
    match: {
      ...(r.trades.length ? { trade: r.trades } : {}),
      ...(r.urgency.length ? { urgency: r.urgency } : {}),
      ...(splitList(r.prefixes).length ? { postal_prefix: splitList(r.prefixes) } : {}),
    },
    action: { vendor_ids: r.vendors },
  }));
}

const rulesInvalid = (rules: RuleDraft[]) => rules.some((r) => r.vendors.length === 0 || splitList(r.prefixes).some((p) => !/^\d{1,5}$/.test(p)));

function RulesSection({
  data,
  rules,
  setRules,
  vendors,
  editable,
}: {
  data: RoutingResponse;
  rules: RuleDraft[];
  setRules: (r: RuleDraft[]) => void;
  vendors: Vendor[];
  editable: boolean;
}) {
  const update = (i: number, r: RuleDraft) => setRules(rules.map((x, j) => (j === i ? r : x)));
  const move = (i: number, delta: -1 | 1) => {
    const next = rules.slice();
    const [item] = next.splice(i, 1);
    if (item) next.splice(i + delta, 0, item);
    setRules(next);
  };

  return (
    <section className="stack" aria-labelledby="rt-rules">
      <div className="row-between">
        <h2 id="rt-rules">Rules</h2>
        {editable ? (
          <button type="button" className="btn" disabled={rules.length >= data.limits.rules} onClick={() => setRules([...rules, newRule()])}>
            Add rule
          </button>
        ) : null}
      </div>
      <p className="small muted">
        Checked top to bottom before the order above; the first rule that matches a lead decides which contractors it may go to, in that order. Example:
        emergency plumbing in ZIP prefix 981 (Seattle) always goes to your on-call plumber.
      </p>
      {rules.length ? (
        <ol className="stack" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {rules.map((r, i) => (
            <RuleCard
              key={r.key}
              rule={r}
              index={i}
              count={rules.length}
              vendors={vendors}
              editable={editable}
              onChange={(next) => update(i, next)}
              onMove={(delta) => move(i, delta)}
              onRemove={() => setRules(rules.filter((_, j) => j !== i))}
            />
          ))}
        </ol>
      ) : (
        <Empty title="No rules">Every lead follows the order above.</Empty>
      )}
    </section>
  );
}

/**
 * The settings and the rules are two endpoints (`PATCH /routing`,
 * `PUT /routing/rules`) behind ONE Save: it sends whichever changed, profile
 * first. A partner never has to know there are two, or wonder which button
 * saved what.
 */
function RoutingEditor({ data, vendors, editable, onSaved }: { data: RoutingResponse; vendors: Vendor[]; editable: boolean; onSaved: (r: RoutingResponse) => void }) {
  const { api } = useSession();
  const toast = useToast();
  const [d, setD] = useState<ProfileDraft>(() => initialDraft(data));
  const [rules, setRules] = useState<RuleDraft[]>(() => rulesFromServer(data));
  // Reset a draft only when the SAVED values change (a save, another tab's
  // save on reload) — not when an identical refetch hands us a new object,
  // which would throw away what the partner is typing. The reset happens
  // during render, not in an effect: a mount-time effect runs after the form
  // is on screen, so an edit made before it flushed was silently wiped.
  const savedKey = JSON.stringify(draftFrom(data));
  const [seenKey, setSeenKey] = useState(savedKey);
  if (seenKey !== savedKey) {
    setSeenKey(savedKey);
    setD(initialDraft(data));
  }
  const rulesKey = JSON.stringify(data.rules);
  const [seenRules, setSeenRules] = useState(rulesKey);
  if (seenRules !== rulesKey) {
    setSeenRules(rulesKey);
    setRules(rulesFromServer(data));
  }

  const profileDirty = JSON.stringify(d) !== savedKey;
  const rulesDirty = JSON.stringify(rulesBody(rules)) !== JSON.stringify(rulesBody(rulesFromServer(data)));
  const invalid = profileIssues(d, data).invalid || rulesInvalid(rules);
  // Leaving asks only about the partner's own edits, not the prefilled suggestion.
  const edited = JSON.stringify(d) !== JSON.stringify(initialDraft(data)) || rulesDirty;
  useUnsavedChangesGuard(editable && edited);

  const save = useMutation(async () => {
    let next = data;
    // Defaults are never saved silently: the settings go only when they
    // differ from what is saved (the prefilled suggestion counts: it is shown
    // as "not saved yet" and saved only by this button).
    if (profileDirty) {
      const saved = await api.updateRouting(profilePatch(d, data));
      next = { ...saved, rules: data.rules };
    }
    if (rulesDirty) {
      const res = await api.replaceRoutingRules(rulesBody(rules));
      next = { ...next, rules: res.rules };
    }
    return next;
  });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const saved = await save.run();
    if (saved) {
      toast('Routing saved');
      onSaved(saved);
    }
  };

  return (
    <form className="stack-lg" onSubmit={submit} noValidate aria-label="Routing settings">
      <div className="stack">
        <ProfileSections data={data} d={d} setD={setD} editable={editable} />
      </div>
      <RulesSection data={data} rules={rules} setRules={setRules} vendors={vendors} editable={editable} />
      <ErrorNotice error={save.error} />
      {editable ? (
        <div className="row" style={{ flexWrap: 'wrap' }}>
          <button type="submit" className="btn btn-primary" disabled={save.pending || invalid || (!profileDirty && !rulesDirty)}>
            {save.pending ? 'Saving…' : 'Save routing'}
          </button>
          {!profileDirty && !rulesDirty ? <span className="small muted">No unsaved changes.</span> : null}
        </div>
      ) : (
        <p className="small muted">Only owners and admins can change routing.</p>
      )}
    </form>
  );
}

export function RoutingRules() {
  const { api, role } = useSession();
  const editable = can(role, 'routing');
  const routing = useQuery(() => api.getRouting(), []);
  const roster = useQuery(() => api.listVendors({ active: 'true', limit: 200 }), []);
  const [showAnyway, setShowAnyway] = useState(false);
  // No active contractors: routing has nothing to route to, so the settings
  // are noise. A failed roster read shows the settings rather than guessing.
  const noContractors = Boolean(roster.data) && roster.data?.items.length === 0 && !(routing.data?.rules.length ?? 0);

  return (
    <div className="stack-lg">
      <PageHead
        title="Routing rules"
        lede="Who gets each lead first, how long they have to accept, and who is offered it next."
      />
      {(routing.loading && !routing.data) || (roster.loading && !roster.data) ? (
        <Loading label="Loading routing" />
      ) : routing.error || !routing.data ? (
        <ErrorNotice error={routing.error} />
      ) : noContractors && !showAnyway ? (
        <section className="panel">
          <Empty level={2} title="No contractors yet">
            <div className="stack" style={{ alignItems: 'center' }}>
              <p style={{ margin: 0 }}>Routing rules apply once you add contractors. Right now every lead goes to your destinations.</p>
              <div className="row" style={{ justifyContent: 'center', flexWrap: 'wrap' }}>
                {can(role, 'roster') ? (
                  <Link className="btn btn-primary" to="/contractors">
                    Add contractors
                  </Link>
                ) : null}
                <button type="button" className="btn btn-ghost" onClick={() => setShowAnyway(true)}>
                  Set up routing now anyway
                </button>
              </div>
            </div>
          </Empty>
        </section>
      ) : (
        <RoutingEditor data={routing.data} vendors={roster.data?.items ?? []} editable={editable} onSaved={(saved) => routing.setData(saved)} />
      )}
    </div>
  );
}
